import { Autocomplete, Button, CircularProgress, Grid, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import React, { FC, Dispatch, SetStateAction, useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import RecentActorsIcon from '@mui/icons-material/RecentActors';
import CategoryIcon from '@mui/icons-material/Category';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import EditAttributesIcon from '@mui/icons-material/EditAttributes';
import { useCategoryStore } from "../../../hooks/useCategoryStore";
import { useSpecificAttributeStore } from "../../../hooks/useSpecificAttributeStore";
import { useProductUsageAttributeStore } from "../../../hooks/useProductUsageAttributeStore";
import { useSearcherStore } from "../../../hooks/useSearcherStore";
import { ApiStatus } from "../../../types/api/status";

interface FiltersV3Props {
    setShowFilters:Dispatch<SetStateAction<boolean>>
    getFilterProducts:(data: any) => Promise<false | undefined>
}

export const FiltersV3:FC<FiltersV3Props> = (props) => {
    const { setShowFilters, getFilterProducts } = props

    const {
        selectedCategory, setSelectedCategorySearcher,
        selectedSubCategory, setSelectedSubCategory,
        selectedSpecificAttribute, setSelectedSpecificAttribute,
        selectedUseAttribute, setSelectedUseAttribute
    } = useSearcherStore()

    const { categories, getCategoriesByCompany } = useCategoryStore()
    const { specificAttributes, getSpecificAttributesByCompany } = useSpecificAttributeStore()
    const { productUsageAttributes, getProductUsageAttributeByCompany } = useProductUsageAttributeStore()

    const [filterCode, setFilterCode] = useState<string>('')
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        getCombosBoxesApi()
    },[])

    useEffect(() => {
        const timer = setTimeout(() => onFilter(), 1000)
        return () => clearTimeout(timer)
    },[filterCode])

    const getCombosBoxesApi = async () => {
        setLoading(true)
        await getCategoriesByCompany()
        await getSpecificAttributesByCompany()
        await getProductUsageAttributeByCompany()
        setLoading(false)
    }

    const onFilter = async () => {
        let payload:any = {}
        if (filterCode !== '') payload = {code: filterCode}
        await getFilterProducts(payload)
    }

    return (
        <Grid item xs={12} md={3} style={{ height: '70vh', backgroundColor: 'whitesmoke', borderRadius: '15px', overflowY: 'scroll' }}>
            <div style={{ width: '100%', margin: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '90%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px' }}>
                    <Typography>Filtros avanzados</Typography>
                    <Tooltip title={'Cerrar panel'}>
                        <IconButton onClick={() => setShowFilters(false)}>
                            <CloseIcon fontSize='medium' style={{ color: '#91CBF9' }} />
                        </IconButton>
                    </Tooltip>
                </div>
                <div style={{ width: '80%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Grid item container sx={{ mt: 3, mb: 3 }}>
                        <Grid item container>
                            <RecentActorsIcon sx={{ color: '#858484', mr: 1 }} />
                            <Typography>Código:</Typography>
                        </Grid>
                        <Grid item container>
                            <TextField
                                id="txt-code"
                                variant="outlined"
                                size='small'
                                fullWidth
                                value={filterCode}
                                onChange={(e) => setFilterCode(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container sx={{ mb: 3 }}>
                        <Grid item container>
                            <CategoryIcon sx={{ color: '#858484', mr: 1 }} />
                            <Typography>Categoría:</Typography>
                        </Grid>
                        <Grid item container>
                            <Autocomplete
                                key='cbx-category'
                                id="cbx-category"
                                size="small"
                                value={selectedCategory || null}
                                options={loading ? [] : categories }
                                getOptionLabel={(option) => option?.name || ''}
                                isOptionEqualToValue={(option,value)=> option.name === value?.name} 
                                renderInput={(params) => 
                                    <TextField {...params} 
                                        label=""
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }} 
                                    />}
                                onChange={(e, newValue) => {
                                    setSelectedCategorySearcher(newValue)
                                    if (newValue == null) setSelectedSubCategory(null)
                                }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Grid item container sx={{ mb: 3 }}>
                        <Grid item container>
                            <AccountTreeIcon sx={{ color: '#858484', mr: 1 }} />
                            <Typography>Subcategoría:</Typography>
                        </Grid>
                        <Grid item container>
                            <Autocomplete
                                id="cbx-sub-category"
                                size="small"
                                value={selectedSubCategory || null}
                                options={selectedCategory?.subcategories || []}
                                getOptionLabel={(option) => option?.name || ''}
                                isOptionEqualToValue={(option,value)=> option.name === value?.name} 
                                renderInput={(params) => 
                                    <TextField {...params} 
                                        label="" 
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                }
                                onChange={(e, newValue: any) => {
                                    setSelectedSubCategory(newValue)
                                }}
                                fullWidth
                                noOptionsText={!selectedCategory ? "Seleccione una categoría" : "No options"}
                            />
                        </Grid>
                    </Grid>
                    <Grid item container sx={{ mb: 3 }}>
                        <Grid item container>
                            <EditAttributesIcon sx={{ color: '#858484', mr: 1 }} />
                            <Typography>Atributo específico:</Typography>
                        </Grid>
                        <Grid item container>
                            <Autocomplete
                                id="cbx-specific-attribute"
                                size="small"
                                value={selectedSpecificAttribute || null}
                                options={loading ? [] : specificAttributes}
                                getOptionLabel={(option) => option?.name || ''}
                                isOptionEqualToValue={(option,value)=> option.name === value?.name} 
                                renderInput={(params) => 
                                    <TextField {...params} 
                                        label=""
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                }
                                onChange={(e, newValue: any) => {
                                    setSelectedUseAttribute(newValue)
                                }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                    <Grid item container sx={{ mb: 3 }}>
                        <Grid item container>
                            <EditAttributesIcon sx={{ color: '#858484', mr: 1 }} />
                            <Typography>Atributo de uso:</Typography>
                        </Grid>
                        <Grid item container>
                            <Autocomplete
                                id="cbx-specific-use"
                                size="small"
                                value={selectedUseAttribute || null}
                                options={loading ? [] : productUsageAttributes}
                                getOptionLabel={(option) => option?.name || ''}
                                isOptionEqualToValue={(option,value)=> option.name === value?.name} 
                                renderInput={(params) => 
                                    <TextField {...params} 
                                        label="" 
                                        size="small"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                }
                                onChange={(e, newValue: any) => {
                                    setSelectedSpecificAttribute(newValue)
                                }}
                                fullWidth
                            />
                        </Grid>
                    </Grid>
                </div>
                <Grid container sx={{ width: '80%', marginTop: '5px', justifyContent: 'flex-end', marginBottom: '20px' }}>
                    <Button
                        sx={{ backgroundColor: '#91CBF9', borderRadius: '10px', color: '#ffffff', height: '25px', padding: '6px 16px', textTransform: 'none', width: 160, "&:hover": { backgroundColor: "#91CBF9" } }}
                        onClick={onFilter}>
                        Filtrar
                    </Button>
                </Grid>
            </div>
        </Grid>
    )
}